import { Component, HostBinding, Input, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import {
    AuthenticationStrengthPolicy,
    ConditionalAccessGrantControl,
    ConditionalAccessPolicy,
} from '@microsoft/microsoft-graph-types-beta';
import { startWith } from 'rxjs/operators';

// TODO, Karim
// password change must be and, and user risk should be enabled
// which properties can't be combined togethers

type BuildControllerValue = ConditionalAccessGrantControl | 'authenticationStrength';
interface BuildController {
    name: string;
    value: BuildControllerValue;
    checked: boolean;
    disabled: boolean;
}

const buildControllersLookup: BuildController[] = [
    { name: 'Require multifactor authentication', value: 'mfa', checked: false, disabled: false },
    { name: 'Require authentication strength', value: 'authenticationStrength', checked: false, disabled: false },
    { name: 'Require device to be marked as compliant', value: 'compliantDevice', checked: false, disabled: false },
    {
        name: 'Require Microsoft Entra hybrid joined device',
        value: 'domainJoinedDevice',
        checked: false,
        disabled: false,
    },
    { name: 'Require app protection policy', value: 'compliantApplication', checked: false, disabled: false },
    // { name: 'Require password change', value: 'passwordChange', checked: false, disabled: false },
];

const multipleControls = {
    AND: 'Require all the selected controls',
    OR: 'Require one of the selected controls',
};

/** Conflicting rules mapping */
const conflictMap: Pick<
    Record<BuildControllerValue, BuildControllerValue[]>,
    'mfa' | 'authenticationStrength' | 'passwordChange'
> = {
    mfa: ['authenticationStrength'],
    authenticationStrength: ['mfa'],
    passwordChange: [...buildControllersLookup.filter((res) => res.value !== 'passwordChange').map((res) => res.value)],
};

@Component({
    selector: 'cap-grant-control',
    templateUrl: './cap-grant-control.component.html',
    styleUrls: ['./cap-grant-control.component.scss', '../../../../../styles/baseline-style.scss'],
})
export class CapGrantControlComponent implements OnInit {
    @HostBinding('style.display') display = 'contents';

    @Input() policy: ConditionalAccessPolicy;
    @Input() readOnly: boolean;
    @Input() form: FormGroup;
    @Input() errorsString: string = '';
    @Input() authenticationStrengthPolicies: AuthenticationStrengthPolicy[] = [];
    @ViewChild('grantPopupRef') grantPopupRef;

    multipleControls = multipleControls;
    configureGrant: boolean = true;

    buildControllers = JSON.parse(JSON.stringify(buildControllersLookup)) as BuildController[];
    desiredAuthenticationStrengthDisplayName: string | undefined;

    authenticationStrengthValue: string;
    grantControl: 'block' | 'grant' = 'block';
    operatorControl = new FormControl(['OR']);

    constructor(private dialog: MatDialog) {}

    ngOnInit(): void {
        this.setInitialValues();
    }

    setInitialValues() {
        this.buildControllers = JSON.parse(JSON.stringify(buildControllersLookup)) as BuildController[];
        this.desiredAuthenticationStrength.valueChanges
            .pipe(startWith(this.desiredAuthenticationStrength.value))
            .subscribe((desiredAuthenticationStrengthIdValue) => {
                this.desiredAuthenticationStrengthDisplayName = this.authenticationStrengthPolicies.find(
                    (re) => re.id === desiredAuthenticationStrengthIdValue,
                )?.displayName;
            });

        this.grantControl = this.desiredMainRadioControl || 'block';
        this.operatorControl.setValue(this.desiredOperatorControlValue || 'OR');

        this.desiredBuiltInControlsValue.forEach((desiredBuildInControl) => {
            const control = this.buildControllers.find((res) => res.value === desiredBuildInControl);

            if (control) {
                control.checked = true;
            }
        });

        if (this.desiredAuthenticationStrengthIdValue) {
            this.buildControllers.find((res) => res.value === 'authenticationStrength').checked = true;
            this.authenticationStrengthValue = this.desiredAuthenticationStrengthIdValue;
        } else {
            // set default to the first item in the array
            this.authenticationStrengthValue = this.authenticationStrengthPolicies[0]?.id;
        }
    }

    public onToggleGrantController() {
        if (!this.configureGrant) {
            this.form.get('grant').get('operator').setValue(null);
            this.form.get('grant').get('authenticationStrength').setValue(undefined);
            this.form.get('grant').get('builtInControls').setValue([]);
        }
    }

    openGrantPopup() {
        const dialogRef$ = this.dialog.open(this.grantPopupRef, {
            position: {
                top: '0',
                right: '0',
            },
            minWidth: '500px',
            width: '30%',
            height: '100%',
            hasBackdrop: true,
        });

        dialogRef$.afterClosed().subscribe((res) => {
            if (res) {
                this.save();
            } else {
                this.setInitialValues();
            }
        });
    }

    toggleMainController() {
        this.operatorControl.setValue('OR');
        this.operatorControl.disable();
        this.buildControllers.forEach((res) => (res.checked = false));
    }

    onCheckboxChange(changedValue: string, checked: boolean) {
        // if grant control = 'block' set it to grant
        this.grantControl = 'grant';

        const numberOfControlsChecked = this.buildControllers.filter((res) => res.checked).length;

        if (numberOfControlsChecked > 1) {
            this.operatorControl.enable();
        } else {
            this.operatorControl.setValue('OR');
            this.operatorControl.disable();
        }

        // Disable conflicting checkboxes
        this.buildControllers.forEach((control) => {
            if (conflictMap[changedValue]?.includes(control.value)) {
                control.disabled = checked;
            }
        });
    }

    save() {
        if (this.grantControl === 'block') {
            this.form.get('grant').get('operator').setValue(this.operatorControl.value);
            this.form.get('grant').get('authenticationStrength').setValue(undefined);
            this.form.get('grant').get('builtInControls').setValue(['block']);
        } else {
            const selectedBuildInControls = this.buildControllers
                .filter((res) => res.checked && !res.disabled && res.value !== 'authenticationStrength')
                .map((res) => res.value);
            this.form.get('grant').get('builtInControls').setValue(selectedBuildInControls);

            const authenticationStrengthEnabled = this.buildControllers.find(
                (res) => res.value === 'authenticationStrength' && res.checked && !res.disabled,
            );

            this.form
                .get('grant')
                .get('authenticationStrength')
                .setValue(authenticationStrengthEnabled ? this.authenticationStrengthValue : undefined);

            if (selectedBuildInControls.length > 0 || authenticationStrengthEnabled) {
                this.form.get('grant').get('operator').setValue(this.operatorControl.value);
            } else {
                this.form.get('grant').get('operator').setValue(null);
            }
        }

        this.dialog.closeAll();
    }

    getBuildInControlName(value: string) {
        return this.buildControllers.find((res) => res.value === value)?.name || value;
    }

    get desiredBuiltInControlsValue(): string[] {
        return (this.form.get('grant').get('builtInControls').value as string[]) || [];
    }

    get desiredMainRadioControl(): 'block' | 'grant' {
        const buildInControls = (this.form.get('grant').get('builtInControls').value as string[]) || [];

        if (this.desiredAuthenticationStrengthIdValue) {
            return 'grant';
        }

        if (buildInControls.length === 0) {
            return 'block';
        }

        return buildInControls.includes('block') ? 'block' : 'grant';
    }

    get desiredOperatorControl(): FormControl {
        return this.form.get('grant').get('operator') as FormControl;
    }

    get desiredOperatorControlValue(): 'AND' | 'OR' {
        return this.form.get('grant').get('operator').value;
    }

    get desiredAuthenticationStrength(): FormControl {
        return this.form.get('grant').get('authenticationStrength') as FormControl;
    }

    get desiredAuthenticationStrengthIdValue(): string {
        return this.form.get('grant').get('authenticationStrength')?.value;
    }

    get currentAuthenticationStrengthDisplayName(): string {
        return this.authenticationStrengthPolicies.find(
            (re) => re.id === this.policy?.grantControls?.authenticationStrength?.id,
        )?.displayName;
    }
}
